import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  updateDoc
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { CURRENCY_SYMBOL } from '../../constants';
import { formatDate, formatDateForInput, formatPrice } from '../../utils';
import { aggregateExpenses } from '../../utils/expenseUtils';
import {
  aggregateWorkshopExpensesFromOrders,
  aggregateWorkshopPayments,
  buildWorkshopOrderRows
} from '../../utils/workshopUtils';
import './ExpenseList.css';

const PAGE_SIZE = 20;

const paginateItems = (items, page) => {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  return {
    items: items.slice(start, start + PAGE_SIZE),
    total,
    totalPages,
    currentPage,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + PAGE_SIZE, total)
  };
};

const ExpensePagination = ({ pagination, onPageChange, t }) => {
  if (pagination.total === 0) return null;

  const { currentPage, totalPages, from, to, total } = pagination;
  const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
    const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
    return page <= totalPages ? page : null;
  }).filter(Boolean);

  return (
    <div className="expense-pagination">
      <span className="expense-pagination-info">
        {t('expenses.pagination', { from, to, total })}
      </span>
      <div className="expense-pagination-controls">
        <button
          type="button"
          className="expense-pagination-btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          <i className="fas fa-chevron-left"></i>
        </button>
        {pageNumbers.map((page) => (
          <button
            key={page}
            type="button"
            className={`expense-pagination-btn ${currentPage === page ? 'active' : ''}`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          className="expense-pagination-btn"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>
  );
};

const getTodayInputDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const emptyForm = () => ({
  title: '',
  expenseDate: getTodayInputDate(),
  amount: ''
});

const emptyPaymentForm = () => ({
  paymentDate: getTodayInputDate(),
  amount: '',
  note: ''
});

const getExpenseTimestamp = (expense) => {
  if (expense?.createdAt?.toDate) return expense.createdAt.toDate().getTime();
  if (expense?.createdAt?.seconds) return expense.createdAt.seconds * 1000;
  if (expense?.createdAt instanceof Date) return expense.createdAt.getTime();
  return 0;
};

const sortExpenses = (items) =>
  [...items].sort((a, b) => {
    const dateA = new Date(formatDateForInput(a.expenseDate) || 0).getTime();
    const dateB = new Date(formatDateForInput(b.expenseDate) || 0).getTime();
    if (dateB !== dateA) return dateB - dateA;
    return getExpenseTimestamp(b) - getExpenseTimestamp(a);
  });

const mapSnapshotToExpenses = (snapshot) => {
  const byId = new Map();
  snapshot.docs.forEach((item) => {
    byId.set(item.id, { id: item.id, ...item.data() });
  });
  return sortExpenses([...byId.values()]);
};

const sortWorkshopPayments = (items) =>
  [...items].sort((a, b) => {
    const dateA = new Date(formatDateForInput(a.paymentDate) || 0).getTime();
    const dateB = new Date(formatDateForInput(b.paymentDate) || 0).getTime();
    return dateB - dateA;
  });

const ExpenseList = ({ onExpenseChange }) => {
  const { user, permissions } = useAuth();
  const { t } = useTranslation();
  const canManage = permissions?.canManageExpenses;

  const [expenses, setExpenses] = useState([]);
  const [workshopPayments, setWorkshopPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [adminSettings, setAdminSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [workshopLoading, setWorkshopLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePaymentConfirm, setDeletePaymentConfirm] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm());
  const [notification, setNotification] = useState(null);
  const [workshopOpen, setWorkshopOpen] = useState(true);
  const [manualOpen, setManualOpen] = useState(true);
  const [workshopPage, setWorkshopPage] = useState(1);
  const [workshopPaymentsPage, setWorkshopPaymentsPage] = useState(1);
  const [manualPage, setManualPage] = useState(1);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'expenses'),
      (snapshot) => {
        setExpenses(mapSnapshotToExpenses(snapshot));
        setLoading(false);
      },
      (error) => {
        console.error('Giderler dinlenirken hata:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'workshopPayments'),
      (snapshot) => {
        const items = sortWorkshopPayments(
          snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
        );
        setWorkshopPayments(items);
      },
      (error) => console.error('Atölye ödemeleri dinlenirken hata:', error)
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      try {
        const settingsSnap = await getDoc(doc(db, 'settings', 'global'));
        if (!cancelled) {
          setAdminSettings(settingsSnap.exists() ? settingsSnap.data() : {});
        }
      } catch (error) {
        console.error('Atölye ayarları yüklenirken hata:', error);
      }
    };

    loadSettings();

    const unsubscribeOrders = onSnapshot(
      collection(db, 'orders'),
      (snapshot) => {
        setOrders(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setWorkshopLoading(false);
      },
      (error) => {
        console.error('Siparişler dinlenirken hata:', error);
        setWorkshopLoading(false);
      }
    );

    const unsubscribeCustomers = onSnapshot(
      collection(db, 'customers'),
      (snapshot) => {
        setCustomers(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      },
      (error) => console.error('Müşteriler dinlenirken hata:', error)
    );

    return () => {
      cancelled = true;
      unsubscribeOrders();
      unsubscribeCustomers();
    };
  }, []);

  const manualSummary = useMemo(() => aggregateExpenses(expenses), [expenses]);

  const workshopSummary = useMemo(() => {
    const accrued = aggregateWorkshopExpensesFromOrders(orders, customers, adminSettings);
    const paid = aggregateWorkshopPayments(workshopPayments);
    const remaining = Math.max(0, Number((accrued.totalAllTime - paid).toFixed(2)));
    return { ...accrued, paid, remaining };
  }, [orders, customers, adminSettings, workshopPayments]);

  const combinedSummary = useMemo(() => ({
    totalMonth: Number((manualSummary.totalMonth + workshopSummary.totalMonth).toFixed(2)),
    totalYear: Number((manualSummary.totalYear + workshopSummary.totalYear).toFixed(2)),
    totalAllTime: Number((manualSummary.totalAllTime + workshopSummary.totalAllTime).toFixed(2))
  }), [manualSummary, workshopSummary]);

  const workshopOrderRows = useMemo(
    () => buildWorkshopOrderRows(orders, customers, adminSettings),
    [orders, customers, adminSettings]
  );

  const workshopOrdersPagination = useMemo(
    () => paginateItems(workshopOrderRows, workshopPage),
    [workshopOrderRows, workshopPage]
  );

  const workshopPaymentsPagination = useMemo(
    () => paginateItems(workshopPayments, workshopPaymentsPage),
    [workshopPayments, workshopPaymentsPage]
  );

  const manualPagination = useMemo(
    () => paginateItems(expenses, manualPage),
    [expenses, manualPage]
  );

  useEffect(() => {
    setWorkshopPage((page) => Math.min(page, workshopOrdersPagination.totalPages));
  }, [workshopOrdersPagination.totalPages]);

  useEffect(() => {
    setWorkshopPaymentsPage((page) => Math.min(page, workshopPaymentsPagination.totalPages));
  }, [workshopPaymentsPagination.totalPages]);

  useEffect(() => {
    setManualPage((page) => Math.min(page, manualPagination.totalPages));
  }, [manualPagination.totalPages]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const notifyExpenseChange = () => {
    if (onExpenseChange) {
      window.setTimeout(() => onExpenseChange(), 0);
    }
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditingExpense(null);
    setShowForm(false);
  };

  const resetPaymentForm = () => {
    setPaymentForm(emptyPaymentForm());
    setShowPaymentForm(false);
  };

  const openCreateForm = () => {
    setEditingExpense(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEditForm = (expense) => {
    setEditingExpense(expense);
    setForm({
      title: expense.title || '',
      expenseDate: formatDateForInput(expense.expenseDate) || getTodayInputDate(),
      amount: expense.amount != null ? String(expense.amount) : ''
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage) return;

    const title = form.title.trim();
    const amount = parseFloat(form.amount);
    if (!title) {
      showNotification(t('expenses.notifications.titleRequired'), 'warning');
      return;
    }
    if (!form.expenseDate) {
      showNotification(t('expenses.notifications.dateRequired'), 'warning');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      showNotification(t('expenses.notifications.amountRequired'), 'warning');
      return;
    }

    const payload = {
      title,
      expenseDate: form.expenseDate,
      amount: Number(amount.toFixed(2)),
      updatedAt: new Date()
    };

    try {
      setSaving(true);
      if (editingExpense) {
        await updateDoc(doc(db, 'expenses', editingExpense.id), payload);
        showNotification(t('expenses.notifications.updated'));
      } else {
        await addDoc(collection(db, 'expenses'), {
          ...payload,
          createdBy: user?.uid || null,
          createdAt: new Date()
        });
        showNotification(t('expenses.notifications.created'));
      }
      resetForm();
      notifyExpenseChange();
    } catch (error) {
      console.error('Gider kaydedilirken hata:', error);
      showNotification(t('expenses.notifications.saveError', { message: error.message }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!canManage) return;

    const amount = parseFloat(paymentForm.amount);
    if (!paymentForm.paymentDate) {
      showNotification(t('expenses.workshop.dateRequired'), 'warning');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      showNotification(t('expenses.notifications.amountRequired'), 'warning');
      return;
    }

    try {
      setSaving(true);
      await addDoc(collection(db, 'workshopPayments'), {
        amount: Number(amount.toFixed(2)),
        paymentDate: paymentForm.paymentDate,
        note: paymentForm.note.trim(),
        createdBy: user?.uid || null,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      resetPaymentForm();
      showNotification(t('expenses.workshop.paymentSaved'));
    } catch (error) {
      console.error('Atölye ödemesi kaydedilirken hata:', error);
      showNotification(t('expenses.workshop.paymentError', { message: error.message }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm || !canManage) return;
    try {
      setSaving(true);
      await deleteDoc(doc(db, 'expenses', deleteConfirm.id));
      setDeleteConfirm(null);
      showNotification(t('expenses.notifications.deleted'));
      notifyExpenseChange();
    } catch (error) {
      console.error('Gider silinirken hata:', error);
      showNotification(t('expenses.notifications.deleteError', { message: error.message }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentConfirm || !canManage) return;
    try {
      setSaving(true);
      await deleteDoc(doc(db, 'workshopPayments', deletePaymentConfirm.id));
      setDeletePaymentConfirm(null);
      showNotification(t('expenses.workshop.paymentDeleted'));
    } catch (error) {
      console.error('Atölye ödemesi silinirken hata:', error);
      showNotification(t('expenses.workshop.paymentDeleteError', { message: error.message }), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="expense-list-page">
      {notification && (
        <div className={`toast-notification ${notification.type}`}>
          <div className="toast-content">
            <div className="toast-message">{notification.message}</div>
            <button type="button" className="toast-close" onClick={() => setNotification(null)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      <div className="expense-list-header">
        <div className="expense-list-title">
          <h2><i className="fas fa-receipt"></i> {t('expenses.title')}</h2>
          <p>{t('expenses.subtitle')}</p>
        </div>
      </div>

      <div className="expense-summary-grid">
        <div className="expense-summary-card">
          <span className="expense-summary-label">{t('expenses.summary.thisMonth')}</span>
          <strong>{formatPrice(combinedSummary.totalMonth)} {CURRENCY_SYMBOL}</strong>
        </div>
        <div className="expense-summary-card">
          <span className="expense-summary-label">{t('expenses.summary.thisYear')}</span>
          <strong>{formatPrice(combinedSummary.totalYear)} {CURRENCY_SYMBOL}</strong>
        </div>
        <div className="expense-summary-card">
          <span className="expense-summary-label">{t('expenses.summary.allTime')}</span>
          <strong>{formatPrice(combinedSummary.totalAllTime)} {CURRENCY_SYMBOL}</strong>
        </div>
      </div>

      <div className={`expense-accordion ${workshopOpen ? 'expanded' : ''}`}>
        <button
          type="button"
          className="expense-accordion-trigger"
          onClick={() => setWorkshopOpen((open) => !open)}
          aria-expanded={workshopOpen}
        >
          <div className="expense-accordion-heading">
            <span className="expense-accordion-icon workshop"><i className="fas fa-scissors"></i></span>
            <div>
              <strong>{t('expenses.workshop.title')}</strong>
              <span className="expense-accordion-meta">
                {t('expenses.recordCount', { count: workshopOrderRows.length })}
              </span>
            </div>
          </div>
          <i className="fas fa-chevron-down expense-accordion-chevron" />
        </button>

        {workshopOpen && (
          <div className="expense-accordion-body">
            <p className="expense-accordion-subtitle">{t('expenses.workshop.subtitle')}</p>

            <div className="workshop-expense-toolbar">
              {canManage && (
                <button type="button" className="expense-add-btn workshop-pay-btn" onClick={() => setShowPaymentForm(true)}>
                  <i className="fas fa-hand-holding-usd"></i> {t('expenses.workshop.addPayment')}
                </button>
              )}
            </div>

            <div className="workshop-summary-grid">
              <div className="workshop-summary-card total">
                <span>{t('expenses.workshop.totalSewing')}</span>
                <strong>
                  {workshopLoading ? '...' : `${formatPrice(workshopSummary.totalAllTime)} ${CURRENCY_SYMBOL}`}
                </strong>
              </div>
              <div className="workshop-summary-card weekly">
                <span>{t('expenses.workshop.weeklySewing')}</span>
                <strong>
                  {workshopLoading ? '...' : `${formatPrice(workshopSummary.totalWeek)} ${CURRENCY_SYMBOL}`}
                </strong>
              </div>
              <div className="workshop-summary-card monthly">
                <span>{t('expenses.workshop.monthlySewing')}</span>
                <strong>
                  {workshopLoading ? '...' : `${formatPrice(workshopSummary.totalMonth)} ${CURRENCY_SYMBOL}`}
                </strong>
              </div>
              <div className="workshop-summary-card paid">
                <span>{t('expenses.workshop.paid')}</span>
                <strong>{formatPrice(workshopSummary.paid)} {CURRENCY_SYMBOL}</strong>
              </div>
              <div className="workshop-summary-card remaining">
                <span>{t('expenses.workshop.remaining')}</span>
                <strong>
                  {workshopLoading ? '...' : `${formatPrice(workshopSummary.remaining)} ${CURRENCY_SYMBOL}`}
                </strong>
              </div>
            </div>

            {showPaymentForm && canManage && (
              <div className="expense-form-card workshop-payment-form">
                <div className="expense-form-header">
                  <h3>{t('expenses.workshop.newPayment')}</h3>
                  <button type="button" className="expense-form-close" onClick={resetPaymentForm}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <form className="expense-form-grid" onSubmit={handlePaymentSubmit}>
                  <div className="form-group">
                    <label>{t('expenses.workshop.paymentDate')}</label>
                    <input
                      type="date"
                      value={paymentForm.paymentDate}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('expenses.form.amount')}</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      placeholder="0.00"
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('expenses.workshop.note')}</label>
                    <input
                      type="text"
                      value={paymentForm.note}
                      onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })}
                      placeholder={t('expenses.workshop.notePlaceholder')}
                      disabled={saving}
                    />
                  </div>
                  <div className="expense-form-actions">
                    <button type="button" className="expense-btn-secondary" onClick={resetPaymentForm} disabled={saving}>
                      {t('common.cancel')}
                    </button>
                    <button type="submit" className="expense-btn-primary" disabled={saving}>
                      {saving ? t('common.saving') : t('expenses.workshop.savePayment')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="expense-table-section">
              <h4>{t('expenses.workshop.orderList')}</h4>
              <div className="expense-table-wrapper">
                {workshopLoading ? (
                  <div className="expense-empty-state">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>{t('common.loading')}</p>
                  </div>
                ) : workshopOrderRows.length === 0 ? (
                  <div className="expense-empty-state">
                    <i className="fas fa-scissors"></i>
                    <p>{t('expenses.workshop.emptyOrders')}</p>
                  </div>
                ) : (
                  <table className="expense-table">
                    <thead>
                      <tr>
                        <th>{t('expenses.workshop.table.customer')}</th>
                        <th>{t('expenses.workshop.table.product')}</th>
                        <th>{t('expenses.workshop.table.usedM2')}</th>
                        <th>{t('expenses.workshop.table.orderDate')}</th>
                        <th>{t('expenses.table.amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workshopOrdersPagination.items.map((row) => (
                        <tr key={row.id}>
                          <td data-label={t('expenses.workshop.table.customer')}>{row.customerName}</td>
                          <td data-label={t('expenses.workshop.table.product')}>{row.productName}</td>
                          <td data-label={t('expenses.workshop.table.usedM2')}>
                            {formatPrice(row.usedM2)} m
                            {row.quantity > 1 ? ` × ${row.quantity}` : ''}
                          </td>
                          <td data-label={t('expenses.workshop.table.orderDate')}>
                            {row.orderDate ? formatDate(row.orderDate) : '—'}
                          </td>
                          <td data-label={t('expenses.table.amount')} className="expense-amount-cell">
                            {formatPrice(row.amount)} {CURRENCY_SYMBOL}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <ExpensePagination
                pagination={workshopOrdersPagination}
                onPageChange={setWorkshopPage}
                t={t}
              />
            </div>

            <div className="expense-table-section">
              <h4>{t('expenses.workshop.paymentHistory')}</h4>
              <div className="expense-table-wrapper">
                {workshopPayments.length === 0 ? (
                  <div className="expense-empty-state">
                    <i className="fas fa-hand-holding-usd"></i>
                    <p>{t('expenses.workshop.emptyPayments')}</p>
                  </div>
                ) : (
                  <table className="expense-table workshop-payments-table">
                    <thead>
                      <tr>
                        <th>{t('expenses.workshop.paymentDate')}</th>
                        <th>{t('expenses.table.amount')}</th>
                        <th>{t('expenses.workshop.note')}</th>
                        {canManage && <th>{t('expenses.table.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {workshopPaymentsPagination.items.map((payment) => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.paymentDate)}</td>
                          <td className="expense-amount-cell">{formatPrice(payment.amount)} {CURRENCY_SYMBOL}</td>
                          <td>{payment.note || '—'}</td>
                          {canManage && (
                            <td className="expense-actions-cell">
                              <button type="button" className="expense-icon-btn delete" onClick={() => setDeletePaymentConfirm(payment)}>
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <ExpensePagination
                pagination={workshopPaymentsPagination}
                onPageChange={setWorkshopPaymentsPage}
                t={t}
              />
            </div>
          </div>
        )}
      </div>

      <div className={`expense-accordion ${manualOpen ? 'expanded' : ''}`}>
        <button
          type="button"
          className="expense-accordion-trigger"
          onClick={() => setManualOpen((open) => !open)}
          aria-expanded={manualOpen}
        >
          <div className="expense-accordion-heading">
            <span className="expense-accordion-icon manual"><i className="fas fa-wallet"></i></span>
            <div>
              <strong>{t('expenses.manualTitle')}</strong>
              <span className="expense-accordion-meta">
                {t('expenses.recordCount', { count: expenses.length })}
              </span>
            </div>
          </div>
          <i className="fas fa-chevron-down expense-accordion-chevron" />
        </button>

        {manualOpen && (
          <div className="expense-accordion-body">
            <div className="manual-expenses-toolbar">
              {canManage && (
                <button type="button" className="expense-add-btn" onClick={openCreateForm}>
                  <i className="fas fa-plus"></i> {t('expenses.addExpense')}
                </button>
              )}
            </div>

            {showForm && canManage && (
              <div className="expense-form-card">
                <div className="expense-form-header">
                  <h3>{editingExpense ? t('expenses.editExpense') : t('expenses.newExpense')}</h3>
                  <button type="button" className="expense-form-close" onClick={resetForm}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <form className="expense-form-grid" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>{t('expenses.form.title')}</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder={t('expenses.form.titlePlaceholder')}
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('expenses.form.date')}</label>
                    <input
                      type="date"
                      value={form.expenseDate}
                      onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('expenses.form.amount')}</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      placeholder="0.00"
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className="expense-form-actions">
                    <button type="button" className="expense-btn-secondary" onClick={resetForm} disabled={saving}>
                      {t('common.cancel')}
                    </button>
                    <button type="submit" className="expense-btn-primary" disabled={saving}>
                      {saving ? t('common.saving') : (editingExpense ? t('common.save') : t('expenses.form.submit'))}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="expense-table-wrapper">
              {loading ? (
                <div className="expense-empty-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>{t('common.loading')}</p>
                </div>
              ) : expenses.length === 0 ? (
                <div className="expense-empty-state">
                  <i className="fas fa-receipt"></i>
                  <p>{t('expenses.empty')}</p>
                </div>
              ) : (
                <table className="expense-table">
                  <thead>
                    <tr>
                      <th>{t('expenses.table.title')}</th>
                      <th>{t('expenses.table.date')}</th>
                      <th>{t('expenses.table.amount')}</th>
                      {canManage && <th>{t('expenses.table.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {manualPagination.items.map((expense) => (
                      <tr key={expense.id}>
                        <td data-label={t('expenses.table.title')}>{expense.title}</td>
                        <td data-label={t('expenses.table.date')}>{formatDate(expense.expenseDate)}</td>
                        <td data-label={t('expenses.table.amount')} className="expense-amount-cell">
                          {formatPrice(expense.amount)} {CURRENCY_SYMBOL}
                        </td>
                        {canManage && (
                          <td data-label={t('expenses.table.actions')} className="expense-actions-cell">
                            <button type="button" className="expense-icon-btn edit" onClick={() => openEditForm(expense)}>
                              <i className="fas fa-pencil-alt"></i>
                            </button>
                            <button type="button" className="expense-icon-btn delete" onClick={() => setDeleteConfirm(expense)}>
                              <i className="fas fa-trash"></i>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <ExpensePagination
              pagination={manualPagination}
              onPageChange={setManualPage}
              t={t}
            />
          </div>
        )}
      </div>

      {deleteConfirm && (
        <div className="expense-modal-overlay">
          <div className="expense-modal">
            <h3>{t('expenses.deleteModal.title')}</h3>
            <p>{t('expenses.deleteModal.message', { title: deleteConfirm.title })}</p>
            <div className="expense-modal-actions">
              <button type="button" className="expense-btn-secondary" onClick={() => setDeleteConfirm(null)} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="button" className="expense-btn-danger" onClick={handleDelete} disabled={saving}>
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletePaymentConfirm && (
        <div className="expense-modal-overlay">
          <div className="expense-modal">
            <h3>{t('expenses.workshop.deletePaymentTitle')}</h3>
            <p>{t('expenses.workshop.deletePaymentMessage', { amount: formatPrice(deletePaymentConfirm.amount) })}</p>
            <div className="expense-modal-actions">
              <button type="button" className="expense-btn-secondary" onClick={() => setDeletePaymentConfirm(null)} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button type="button" className="expense-btn-danger" onClick={handleDeletePayment} disabled={saving}>
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseList;
